/// <reference types="vite/client" />
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import '~/styles/app.css';
import '~/styles/layout.css';
import '~/styles/pages.css';

import {
  EAccountUpdateType,
  getCookie,
  IS_LOGGED_COOKIE_NAME,
  jsonParseSafe,
  LANG_PROP_NAME,
  MFA_PENDING_COOKIE_NAME,
  normalizeLocale,
  parseAccountRestrictionPayload,
  parseBlockedAccountPayload,
  type UserReturnDto,
} from '@skillarea/lib-shared';
import {
  ModalProvider,
  ModalRoot,
  Toaster,
  ToasterProvider,
  LangSaver,
  UTMTagSaver,
} from '@skillarea/lib-frontend';
import { I18nextProvider } from 'react-i18next';
import { ROOT_ELEMENT_ID } from '@skillarea/lib-shared';
import {
  QueryClient,
  QueryClientProvider,
  useQuery,
} from '@tanstack/react-query';
import {
  Outlet,
  HeadContent,
  Link,
  Scripts,
  createRootRouteWithContext,
  redirect,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router';

const GOOGLE_FONTS_CSS_URL =
  'https://fonts.googleapis.com/css2?family=Noto+Sans+Georgian:wght@400;500;600;700&family=Open+Sans:wght@400;500;600;700&family=Press+Start+2P&family=Cormorant:ital,wght@0,400;0,500;0,600;0,700;1,400&display=swap';

import {
  hasAuthRedirectParams,
  parseAuthRedirectParams,
  stashPendingAuthRedirect,
  stripAuthRedirectParams,
} from '~/lib/analytics/auth-redirect';
import {
  pushGa4Event,
  pushPageView,
  pushToDataLayer,
  GTM_ID,
} from '~/lib/analytics/gtm';
import { Footer } from '~/components/Footer/Footer';
import { FormFieldTextsBridge } from '~/components/FormFieldTextsBridge/FormFieldTextsBridge';
import { AccountBlockedModal } from '~/components/AccountBlockedModal/AccountBlockedModal';
import { Header } from '~/components/Header/Header';
import { OnboardModal } from '~/components/OnboardModal';
import { PaymentResultModal } from '~/components/PaymentResultModal';
import { GoPlayModal } from '~/components/GoPlayModal/GoPlayModal';
import { WinMoreModal } from '~/components/WinMoreModal/WinMoreModal';
import { WinRealMoneyModal } from '~/components/WinRealMoneyModal/WinRealMoneyModal';
import { ChestUnlockedModal } from '~/components/ChestUnlockedModal/ChestUnlockedModal';
import { CookieConsent } from '~/components/CookieConsent/CookieConsent';
import { useHeaderUser } from '~/hooks/useHeaderUser';
import { isOnboardModalHash, useOnboardModal } from '~/hooks/useOnboardModal';
import { useCookieConsentTexts } from '~/hooks/texts/useCookieConsentTexts';
import { fetchAuthUser } from '~/lib/auth';
import {
  hasSeenGoPlayModal,
  markGoPlayModalSeen,
  clearPendingChestUnlockedModalToday,
  clearPendingWinMoreModalToday,
  clearPendingWinRealMoneyModalToday,
  hasPendingChestUnlockedModalToday,
  hasPendingWinMoreModalToday,
  hasPendingWinRealMoneyModalToday,
  hasSeenWinMoreModalToday,
  hasSeenWinRealMoneyModalToday,
  markWinMoreModalSeenToday,
  markWinRealMoneyModalSeenToday,
} from '~/lib/one-time-modal-storage';
import {
  DOB_ROUTE_PATH,
  getDobRedirectTarget,
  isDobGateProtectedPath,
  isDobGateRequired,
} from '~/lib/auth/dobRouteLock';
import { initTawkWidget, setTawkUserAttributes } from '~/lib/tawk';
import { initPushNotifications } from '~/lib/mobile/push-notifications';
import { accountBlockedStore } from '~/stores/accountBlockedStore';
import { onboardStore, useOnboardStore } from '~/stores/onboardStore';
import {
  useRequiresDateOfBirth,
  useUserEmail,
  useUserId,
  useUserName,
  userStore,
} from '~/stores/userStore';
import { accountStore } from '~/stores/accountStore';
import { MaintenanceBanner } from '~/components/MaintenanceBanner/MaintenanceBanner';
import { OverlapBanner } from '~/components/OverlapBanner/OverlapBanner';
import { ServerColorIndicator } from '~/components/ServerColorIndicator/ServerColorIndicator';
import { maintenanceStore } from '~/stores/maintenanceStore';
import { overlapStore } from '~/stores/overlapStore';
import { gameQueries } from '~/stores/gameStore';
import { initServiceOptions } from '~/stores/_init';
import i18n, { applyLocale, ensureI18nLocale } from '~/common/i18n';
import { parseLangCookieFromDocument, resolveClientLocale } from '~/lib/locale';
import { persistLocaleCookie } from '~/lib/persistLocaleCookie';
// Ensure socket.io-client is included in the bundle
import 'socket.io-client';
import { cookiesStore } from '~/stores/cookiesStore';

initServiceOptions();

export interface IMyRouterContext {
  queryClient: QueryClient;
  user: UserReturnDto | null;
  locale: string;
}

type TRootBeforeLoadOpts = {
  serverContext?: { user: UserReturnDto | null; locale?: string };
};

type TOneTimeModal =
  | 'go-play'
  | 'chest-unlocked'
  | 'win-more'
  | 'win-real-money'
  | null;

function isPlayHash(hash: string): boolean {
  const normalizedHash = hash.replace(/^#/, '');
  return normalizedHash === 'play' || normalizedHash === 'matchmaking';
}

function isPwaEnabled(): boolean {
  const value = import.meta.env.VITE_IS_PWA_ENABLED;
  return typeof value === 'string' && value === 'true';
}

export const Route = createRootRouteWithContext<IMyRouterContext>()({
  beforeLoad: async (opts) => {
    const serverContext = (opts as TRootBeforeLoadOpts).serverContext;
    const serverUser = serverContext?.user ?? null;

    const preliminaryLocale =
      serverContext?.locale ?? resolveClientLocale(serverUser);
    ensureI18nLocale(preliminaryLocale);

    const user = serverUser ?? (await fetchAuthUser());

    if (
      isDobGateRequired(user) &&
      isDobGateProtectedPath(opts.location.pathname)
    ) {
      throw redirect({
        to: DOB_ROUTE_PATH,
        search: { redirect: getDobRedirectTarget(opts.location.href) },
      });
    }

    const locale = serverContext?.locale ?? resolveClientLocale(user);
    ensureI18nLocale(locale);

    return { user, locale };
  },
  head: () => ({
    meta: [
      {
        charSet: 'utf-8',
      },
      {
        name: 'viewport',
        content: 'width=device-width, initial-scale=1',
      },
      {
        title: 'SkillArea - Skill-based Gaming Platform',
      },
      {
        name: 'description',
        content: 'SkillArea - compete in skill-based games and win real prizes',
      },
      {
        name: 'msapplication-TileImage',
        content: '/favicon/mstile-150x150.png',
      },
      {
        name: 'msapplication-TileColor',
        content: '#ff00b0',
      },
      {
        name: 'theme-color',
        content: '#ff00b0',
      },
    ],
    links: [
      {
        rel: 'preconnect',
        href: 'https://fonts.googleapis.com',
      },
      {
        rel: 'preconnect',
        href: 'https://fonts.gstatic.com',
        crossOrigin: 'anonymous',
      },
      {
        rel: 'preload',
        as: 'style',
        href: GOOGLE_FONTS_CSS_URL,
      },
      {
        rel: 'stylesheet',
        href: GOOGLE_FONTS_CSS_URL,
      },
      {
        rel: 'icon',
        type: 'image/png',
        sizes: '32x32',
        href: '/favicon/favicon-32x32.png',
      },
      {
        rel: 'icon',
        type: 'image/png',
        sizes: '16x16',
        href: '/favicon/favicon-16x16.png',
      },
      { rel: 'icon', href: '/favicon.ico' },
      {
        rel: 'apple-touch-icon',
        sizes: '180x180',
        href: '/favicon/apple-touch-icon.png',
      },
      ...(isPwaEnabled()
        ? [{ rel: 'manifest', href: '/site.webmanifest' }]
        : []),
      {
        rel: 'mask-icon',
        href: '/favicon/safari-pinned-tab.svg',
        color: '#cf0',
      },
    ],
  }),
  component: RootComponent,
});

function RootComponent() {
  return (
    <RootDocument>
      <Outlet />
    </RootDocument>
  );
}

function RootOneTimeModals({
  isAuthHydrated,
  isLoggedIn,
  isStandaloneDobRoute,
  locationHash,
  queryClient,
  requiresDateOfBirth,
  userId,
}: Readonly<{
  isAuthHydrated: boolean;
  isLoggedIn: boolean;
  isStandaloneDobRoute: boolean;
  locationHash: string;
  queryClient: QueryClient;
  requiresDateOfBirth: boolean;
  userId: number;
}>) {
  const navigate = useNavigate();
  const [activeModal, setActiveModal] = useState<TOneTimeModal>(null);
  const isPlayModalOpen = isPlayHash(locationHash);
  const isAccountEntryModalOpen = isOnboardModalHash(locationHash);
  const gamesQuery = useQuery({
    ...gameQueries.list({}, queryClient),
    enabled: activeModal === 'go-play',
  });

  useEffect(() => {
    if (
      !isAuthHydrated ||
      !isLoggedIn ||
      !userId ||
      requiresDateOfBirth ||
      isStandaloneDobRoute ||
      isAccountEntryModalOpen ||
      isPlayModalOpen ||
      activeModal
    ) {
      return;
    }

    if (hasPendingChestUnlockedModalToday(userId)) {
      setActiveModal('chest-unlocked');
      return;
    }

    if (
      hasPendingWinRealMoneyModalToday(userId) &&
      !hasSeenWinRealMoneyModalToday(userId)
    ) {
      setActiveModal('win-real-money');
      return;
    }

    if (
      hasPendingWinMoreModalToday(userId) &&
      !hasSeenWinMoreModalToday(userId)
    ) {
      setActiveModal('win-more');
      return;
    }

    if (!hasSeenGoPlayModal(userId)) {
      setActiveModal('go-play');
    }
  }, [
    activeModal,
    isAuthHydrated,
    isAccountEntryModalOpen,
    isLoggedIn,
    isPlayModalOpen,
    isStandaloneDobRoute,
    requiresDateOfBirth,
    userId,
  ]);

  const markActiveModalConsumed = () => {
    if (!userId) return;

    if (activeModal === 'go-play') {
      markGoPlayModalSeen(userId);
      return;
    }

    if (activeModal === 'win-more') {
      markWinMoreModalSeenToday(userId);
      clearPendingWinMoreModalToday(userId);
    }

    if (activeModal === 'win-real-money') {
      markWinRealMoneyModalSeenToday(userId);
      clearPendingWinRealMoneyModalToday(userId);
    }

    if (activeModal === 'chest-unlocked') {
      clearPendingChestUnlockedModalToday(userId);
    }
  };

  const handleCloseModal = () => {
    markActiveModalConsumed();
    setActiveModal(null);
  };

  const handleAddFunds = () => {
    markActiveModalConsumed();
    setActiveModal(null);
    void navigate({ to: '/account/balance/deposit' });
  };

  return (
    <>
      <ChestUnlockedModal
        isOpen={activeModal === 'chest-unlocked'}
        onClose={handleCloseModal}
        onGoToRewards={() => {
          markActiveModalConsumed();
          setActiveModal(null);
          void navigate({ to: '/', hash: 'rewards' });
        }}
      />
      <GoPlayModal
        gamesData={gamesQuery.data}
        isGamesLoading={
          gamesQuery.isLoading || (gamesQuery.isFetching && !gamesQuery.data)
        }
        isOpen={activeModal === 'go-play'}
        onClose={handleCloseModal}
      />
      <WinMoreModal
        isOpen={activeModal === 'win-more'}
        onAddFunds={handleAddFunds}
        onClose={handleCloseModal}
      />
      <WinRealMoneyModal
        isOpen={activeModal === 'win-real-money'}
        onAddFunds={handleAddFunds}
        onClose={handleCloseModal}
      />
    </>
  );
}

function RootDocument({ children }: Readonly<{ children: ReactNode }>) {
  const { locale, queryClient, user: contextUser } = Route.useRouteContext();
  const { openLoginModal, openRegisterModal } = useOnboardModal();
  const navigate = useNavigate();
  const isLoggedInStore = useOnboardStore((s) => s.isLoggedIn);
  const signOutWithConfirm = useOnboardStore((s) => s.signOutWithConfirm);
  const isServer = typeof window === 'undefined';
  const [isAuthHydrated, setIsAuthHydrated] = useState(false);
  const [htmlLang, setHtmlLang] = useState(locale);
  const userId = useUserId();
  const userName = useUserName();
  const userEmail = useUserEmail();
  const requiresDateOfBirth = useRequiresDateOfBirth();
  const isLoggedIn = isServer
    ? Boolean(contextUser)
    : isAuthHydrated
      ? Boolean(isLoggedInStore)
      : Boolean(contextUser);

  const location = useRouterState({ select: (s) => s.location });
  const locationHash = location.hash;
  const locationHref = location.href;
  const isStandaloneDobRoute = location.pathname === DOB_ROUTE_PATH;
  const lastPushedUrl = useRef<string>('');
  const lastPushedUserId = useRef<string>('');
  const applyProfileLangOnNextSyncRef = useRef(false);
  const wasLoggedInRef = useRef(isLoggedIn);
  const policyElement = useMemo(() => <Link to="/privacy" />, []);
  const cookieConsentTexts = useCookieConsentTexts(policyElement);

  const handleLocaleChange = async (nextLocale: string) => {
    const resolved = applyLocale(nextLocale);
    setHtmlLang(resolved);
    await persistLocaleCookie(resolved, { syncUserProfile: isLoggedIn });
  };

  const syncUserLocale = async (user: UserReturnDto | null) => {
    if (typeof window === 'undefined') {
      return;
    }

    const queryLang = new window.URLSearchParams(window.location.search).get(
      LANG_PROP_NAME,
    );
    if (queryLang) {
      return;
    }

    if (applyProfileLangOnNextSyncRef.current && user?.lang?.current) {
      applyProfileLangOnNextSyncRef.current = false;
      const resolved = applyLocale(user.lang.current);
      setHtmlLang(resolved);
      await persistLocaleCookie(resolved);
      return;
    }

    const cookieCurrent = normalizeLocale(
      parseLangCookieFromDocument()?.current,
    );
    if (cookieCurrent) {
      const resolved = applyLocale(cookieCurrent);
      setHtmlLang(resolved);
      return;
    }

    if (!user?.lang?.current) {
      return;
    }

    const resolved = applyLocale(user.lang.current);
    setHtmlLang(resolved);
    await persistLocaleCookie(resolved);
  };

  useEffect(() => {
    if (!wasLoggedInRef.current && isLoggedIn) {
      applyProfileLangOnNextSyncRef.current = true;
    }

    wasLoggedInRef.current = isLoggedIn;
  }, [isLoggedIn]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const currentUrl = new URL(locationHref, window.location.origin);
    if (!hasAuthRedirectParams(currentUrl.searchParams)) {
      return;
    }

    const payload = parseAuthRedirectParams(currentUrl.searchParams);
    if (payload) {
      const mfaPendingCookieValue = getCookie(MFA_PENDING_COOKIE_NAME);

      if (mfaPendingCookieValue) {
        stashPendingAuthRedirect(payload, mfaPendingCookieValue);
      } else {
        pushGa4Event(payload.authEvent, { method: payload.authMethod });
      }
    }

    const cleanUrl = stripAuthRedirectParams(currentUrl);
    window.history.replaceState(window.history.state, '', cleanUrl);
  }, [locationHref]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const currentUrl = new URL(locationHref, window.location.origin).href;
    if (lastPushedUrl.current !== currentUrl) {
      lastPushedUrl.current = currentUrl;
      pushPageView(currentUrl);
    }
  }, [locationHref]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!userId) return;

    const normalizedUserId = String(userId);
    if (lastPushedUserId.current === normalizedUserId) return;

    lastPushedUserId.current = normalizedUserId;
    pushToDataLayer({ event: 'user_data_ready', userId: normalizedUserId });
  }, [userId]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (!isAuthHydrated || !isLoggedIn || !userId || !requiresDateOfBirth) {
      return;
    }

    if (!isDobGateProtectedPath(location.pathname)) {
      return;
    }

    void navigate({
      to: DOB_ROUTE_PATH,
      search: {
        redirect: getDobRedirectTarget(window.location.href),
      },
      replace: true,
    });
  }, [
    isAuthHydrated,
    isLoggedIn,
    location.pathname,
    navigate,
    requiresDateOfBirth,
    userId,
  ]);

  useEffect(() => {
    accountBlockedStore.getState().syncFromCookie({
      clearClientSession: true,
    });
  }, []);

  useEffect(() => {
    return maintenanceStore.getState().subscribeStatusEvents();
  }, []);

  useEffect(() => {
    return overlapStore.getState().subscribeOverlapStatus();
  }, []);

  useEffect(() => {
    overlapStore.getState().syncAuthState(isLoggedIn);
  }, [isLoggedIn]);

  useEffect(() => {
    let isActive = true;

    const syncAuthState = async (): Promise<void> => {
      if (contextUser) {
        userStore.getState().setUserData(contextUser);
        await syncUserLocale(contextUser);
        onboardStore.setState({ isLoggedIn: true });
        accountBlockedStore.getState().syncFromCookie();
        if (isActive) {
          setIsAuthHydrated(true);
        }
        return;
      }

      onboardStore.getState().updateIsLoggedIn();

      if (!getCookie(IS_LOGGED_COOKIE_NAME)) {
        onboardStore.setState({ isLoggedIn: false });
        accountBlockedStore.getState().syncFromCookie();
        if (isActive) {
          setIsAuthHydrated(true);
        }
        return;
      }

      try {
        const response = await fetch('/api/user/self', {
          credentials: 'include',
        });

        if (!isActive) {
          return;
        }

        if (!response.ok) {
          if (response.status === 403) {
            const responseText = await response.text();
            const blockedAccount = parseBlockedAccountPayload(
              responseText ? jsonParseSafe<unknown>(responseText) : undefined,
            );

            if (blockedAccount) {
              accountBlockedStore.getState().show(blockedAccount, {
                clearClientSession: true,
              });
            } else {
              accountBlockedStore.getState().syncFromCookie({
                clearClientSession: true,
              });
            }
          } else {
            accountBlockedStore.getState().syncFromCookie();
          }

          onboardStore.setState({ isLoggedIn: false });
          setIsAuthHydrated(true);
          return;
        }

        const payload = (await response.json()) as { data?: UserReturnDto };
        const authUser = payload.data ?? null;

        if (authUser) {
          userStore.getState().setUserData(authUser);
          await syncUserLocale(authUser);
          onboardStore.setState({ isLoggedIn: true });
        } else {
          onboardStore.setState({ isLoggedIn: false });
        }

        accountBlockedStore.getState().syncFromCookie();
        setIsAuthHydrated(true);
      } catch (error) {
        if (!isActive) {
          return;
        }

        console.error('Failed to sync auth state in root route', error);
        onboardStore.setState({ isLoggedIn: false });
        accountBlockedStore.getState().syncFromCookie();
        setIsAuthHydrated(true);
      }
    };

    void syncAuthState();

    return () => {
      isActive = false;
    };
  }, [contextUser]);

  useEffect(() => {
    if (!isLoggedIn) return;
    if (!contextUser) userStore.getState().loadUser();

    return accountStore.getState().subscribeAccountEvents((eventSource) => {
      const restrictionListener = (message: MessageEvent<string>) => {
        const restrictionPayload = parseAccountRestrictionPayload(
          jsonParseSafe<unknown>(message.data),
        );

        if (!restrictionPayload) {
          return;
        }

        accountBlockedStore
          .getState()
          .handleRestrictionEvent(restrictionPayload.banStatus);
      };

      eventSource.addEventListener(
        EAccountUpdateType.restriction,
        restrictionListener,
      );

      return () => {
        eventSource.removeEventListener(
          EAccountUpdateType.restriction,
          restrictionListener,
        );
      };
    });
  }, [isLoggedIn, contextUser]);

  useEffect(() => {
    initTawkWidget();
  }, []);

  useEffect(() => {
    if (!isLoggedIn || !userId) return;
    setTawkUserAttributes({ id: userId, name: userName, email: userEmail });
  }, [isLoggedIn, userEmail, userId, userName]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    void initPushNotifications();
  }, []);

  const headerUserData = useHeaderUser(contextUser, isLoggedIn);

  const handleLoginClick = () => {
    openLoginModal();
  };

  const handleRegisterClick = () => {
    openRegisterModal();
  };

  const handleLogoutClick = async () => {
    await signOutWithConfirm();
  };

  return (
    <html lang={htmlLang}>
      <QueryClientProvider client={queryClient}>
        <ModalProvider>
          <head>
            <HeadContent />
            <script>{`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${GTM_ID}');`}</script>
          </head>
          <body>
            <noscript>
              <iframe
                height="0"
                src={`https://www.googletagmanager.com/ns.html?id=${GTM_ID}`}
                style={{ display: 'none', visibility: 'hidden' }}
                title="GTM"
                width="0"
              />
            </noscript>
            <div id={ROOT_ELEMENT_ID} />
            <ToasterProvider>
              <I18nextProvider i18n={i18n}>
                <FormFieldTextsBridge>
                  <Toaster />
                  <UTMTagSaver
                    cookiesStore={cookiesStore}
                    isLoggedIn={isLoggedIn}
                  />
                  <LangSaver
                    cookiesStore={cookiesStore}
                    isLoggedIn={isLoggedIn}
                    onLocaleChange={handleLocaleChange}
                  />

                  <AccountBlockedModal />
                  <OnboardModal />
                  <PaymentResultModal />
                  <RootOneTimeModals
                    isAuthHydrated={isAuthHydrated}
                    isLoggedIn={isLoggedIn}
                    isStandaloneDobRoute={isStandaloneDobRoute}
                    locationHash={locationHash}
                    queryClient={queryClient}
                    requiresDateOfBirth={requiresDateOfBirth}
                    userId={userId}
                  />
                  {isStandaloneDobRoute ? (
                    <main className="main">{children}</main>
                  ) : (
                    <div className="pageWrapper">
                      <OverlapBanner />
                      <MaintenanceBanner />
                      <Header
                        activeLocale={htmlLang}
                        isAuthenticated={isLoggedIn}
                        isBalanceLoading={isLoggedIn && !isAuthHydrated}
                        onLocaleChange={handleLocaleChange}
                        onLoginClick={handleLoginClick}
                        onLogoutClick={handleLogoutClick}
                        onRegisterClick={handleRegisterClick}
                        user={headerUserData}
                      />
                      <main className="main">{children}</main>
                      <Footer />
                    </div>
                  )}
                  <CookieConsent texts={cookieConsentTexts} />
                  <ModalRoot />
                  <ServerColorIndicator />
                </FormFieldTextsBridge>
              </I18nextProvider>
              <Scripts />
            </ToasterProvider>
          </body>
        </ModalProvider>
      </QueryClientProvider>
    </html>
  );
}
